import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SelectButtonChangeEvent } from 'primeng/selectbutton';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AuthService, EnumValues } from '@card-software/library';
import { UserGroup } from '../../models/authorization/enum/user-group.enum';

import { ProductionInventoryService, ProductionViewMode } from '../../services/base-actions/production-inventory.service';
import { LocalHelperService } from '../../services/local-helper.service';
import { EnumService } from '../../services/enum.service';
import { Translation } from '../../models/translation';
import { Roll } from '../../models/production/roll';
import { ProductionCardResultItem } from '../../models/production/production-card-search';
import { ProductionInventoryFiltersComponent } from '../common/production-inventory-filters/production-inventory-filters.component';
import _ from 'lodash';
import { RollProxyService } from '../../proxies/production/roll.proxy';
import { ProductionCardsProxyService } from '../../proxies/production/production-card.proxy.service';

@Component({
  selector: 'app-production-inventory',
  templateUrl: './production-inventory.component.html',
  styleUrl: './production-inventory.component.scss',
})
export class ProductionInventoryComponent implements OnInit, OnDestroy {
  @ViewChild(ProductionInventoryFiltersComponent) filtersComponent!: ProductionInventoryFiltersComponent;

  isLoading = 0;

  // Filters panel visibility. Kept mounted (CSS-hidden, not *ngIf) while toggled off — the
  // filters component drives triggerSearch() for pagination/view-mode changes, so unmounting it
  // would silently break those controls while the panel is hidden.
  showFilters = true;

  get canExport(): boolean {
    return AuthService.hasRole(UserGroup.ProductionInventoryAdmin);
  }

  // View mode toggle
  viewModeControl: FormControl<ProductionViewMode | null> = new FormControl('roll');
  viewModeTranslations: Record<string, Translation> = {
    roll: { es: 'Rollos', en: 'Rolls' },
    productionCard: { es: 'Tarjetas de Producción', en: 'Production Cards' },
  };
  viewModeOptions: { label: string; value: ProductionViewMode }[] = [];

  // Pagination
  pageSize = 50;
  page = 1;
  totalRecords = 0;
  pageInputValue: number | null = 1;

  // Enum values for display
  rollStatuses: EnumValues = {};
  rollCategories: EnumValues = {};
  rollQualities: EnumValues = {};

  private destroy$ = new Subject<void>();

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private productionCardProxyService: ProductionCardsProxyService,
    private rollProxyService: RollProxyService,
    private translateService: TranslateService,
    public localHelperTranslate: LocalHelperService,
    private messageService: MessageService,
    private enumService: EnumService,
  ) {
    this.initViewModeOptions();
    this.loadEnums();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initViewModeOptions();
      this.loadEnums();
    });
  }

  ngOnInit(): void {
    // Initial search is triggered by the filters component
    this.pageSize = this.productionInventoryService.rollSearchPageSize();
  }

  private loadEnums(): void {
    this.rollStatuses = this.enumService.getEnum('RollStatus', 'production').values;
    this.rollCategories = this.enumService.getEnum('RollCategory', 'production').values;
    this.rollQualities = this.enumService.getEnum('RollQuality', 'production').values;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initViewModeOptions(): void {
    const currentLang = (this.translateService.currentLang || 'en') as keyof Translation;
    this.viewModeOptions = [
      { label: this.viewModeTranslations['roll'][currentLang], value: 'roll' },
      { label: this.viewModeTranslations['productionCard'][currentLang], value: 'productionCard' },
    ];
  }

  // View mode getters
  get currentViewMode(): ProductionViewMode {
    return this.productionInventoryService.viewMode();
  }

  get rollResults(): Roll[] {
    return this.productionInventoryService.rollResults();
  }

  get productionCardResults(): ProductionCardResultItem[] {
    return this.productionInventoryService.productionCardResults();
  }

  get hasResults(): boolean {
    if (this.currentViewMode === 'roll') {
      return this.rollResults.length > 0;
    }
    return this.productionCardResults.length > 0;
  }

  get isLastPage(): boolean {
    return this.page * this.pageSize >= this.totalRecords;
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize) || 1;
  }

  get isFirstPage(): boolean {
    return this.page === 1;
  }

  // View mode change handler
  onViewModeChange(event: SelectButtonChangeEvent): void {
    const newMode = event.value as ProductionViewMode;
    this.productionInventoryService.setViewMode(newMode);
    this.resetPagination();

    // Notify filters component of view mode change
    if (this.filtersComponent) {
      this.filtersComponent.onViewModeChanged();
    }
  }

  // Loading handler from filters component
  onLoadingChange(delta: number): void {
    this.isLoading += delta;
  }

  // Total records handler from filters component
  onTotalRecordsChange(total: number): void {
    this.totalRecords = total;
  }

  // Pagination
  private resetPagination(): void {
    this.page = 1;
    this.pageInputValue = 1;
    this.updateProxyPagination();
  }

  private updateProxyPagination(): void {
    if (this.currentViewMode === 'roll') {
      this.productionInventoryService.rollSearchPage.set(this.page);
      this.productionInventoryService.rollSearchPageSize.set(this.pageSize);
    } else {
      this.productionInventoryService.productionCardSearchPage.set(this.page);
      this.productionInventoryService.productionCardSearchPageSize.set(this.pageSize);
    }
  }

  nextPage(): void {
    this.page++;
    this.pageInputValue = this.page;
    this.updateProxyPagination();
    this.filtersComponent?.triggerSearch();
  }

  prevPage(): void {
    this.page--;
    this.pageInputValue = this.page;
    this.updateProxyPagination();
    this.filtersComponent?.triggerSearch();
  }

  firstPage(): void {
    if (this.page === 1) return;
    this.page = 1;
    this.pageInputValue = 1;
    this.updateProxyPagination();
    this.filtersComponent?.triggerSearch();
  }

  lastPage(): void {
    const lastPageNum = this.totalPages;
    if (this.page === lastPageNum) return;
    this.page = lastPageNum;
    this.pageInputValue = lastPageNum;
    this.updateProxyPagination();
    this.filtersComponent?.triggerSearch();
  }

  goToPage(): void {
    const targetPage = this.pageInputValue;
    if (targetPage === null || targetPage < 1 || targetPage > this.totalPages || targetPage === this.page) {
      this.pageInputValue = this.page;
      return;
    }
    this.page = targetPage;
    this.updateProxyPagination();
    this.filtersComponent?.triggerSearch();
  }

  onPageInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.goToPage();
    }
  }

  setSearchPageSize(event: { rows?: number }): void {
    if (event.rows) {
      this.pageSize = event.rows;
      this.page = 1;
      this.pageInputValue = 1;
      this.updateProxyPagination();
      this.filtersComponent?.triggerSearch();
    }
  }

  resetTable(): void {
    this.page = 1;
    this.pageSize = 50;
    this.pageInputValue = 1;
    this.updateProxyPagination();
    this.filtersComponent?.clearAllFilters();
  }

  // Helper methods for displaying data
  getRollLocationName(item: Roll): string {
    if (item.currentLocationName) {
      return this.localHelperTranslate.getLocalizedValue(item.currentLocationName);
    }
    return item.currentLocationCode || '';
  }

  getRollFabricDisplay(item: Roll): string {
    const fabricCode = item.expectedFabricCode || '';
    const fabricDesc = item.expectedFabricDescription || '';
    if (fabricDesc) {
      return `${fabricCode} - ${fabricDesc}`;
    }
    return fabricCode;
  }

  getRollStatusName(status?: number): string {
    if (_.isNil(status)) {
      return '';
    }
    return this.rollStatuses[status] || status.toString();
  }

  getRollCategoryName(category?: number): string {
    if (_.isNil(category)) {
      return '';
    }
    return this.rollCategories[category] || category.toString();
  }

  getRollQualityName(quality?: number): string {
    if (_.isNil(quality)) {
      return '';
    }
    return this.rollQualities[quality] || quality.toString();
  }

  // Export
  exportToExcel(): void {
    if (!this.filtersComponent) {
      this.messageService.add({
        severity: 'error',
        summary: 'Export Error',
        detail: 'Unable to export. Please try again.',
        life: 3000,
      });
      return;
    }

    this.isLoading++;

    if (this.currentViewMode === 'roll') {
      const filters = this.filtersComponent.rollFilters;
      this.rollProxyService.exportToExcel(filters).subscribe({
        next: (blob) => {
          this.downloadBlob(blob, 'rolls-export.xlsx');
          this.isLoading--;
        },
        error: (error) => {
          console.error('Error exporting rolls:', error);
          this.isLoading--;
        },
      });
    } else {
      const filters = this.filtersComponent.productionCardFilters;
      this.productionCardProxyService.exportToExcel(filters).subscribe({
        next: (blob) => {
          this.downloadBlob(blob, 'production-cards-export.xlsx');
          this.isLoading--;
        },
        error: (error) => {
          console.error('Error exporting production cards:', error);
          this.isLoading--;
        },
      });
    }
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
    this.messageService.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: `File "${filename}" has been downloaded.`,
      life: 3000,
    });
  }
}
